import {
    type CmmArtifactFinalPageExperiment_ExperimentParam_Value,
    type Guide,
    type OnboardingChatResults,
} from '@fruitful/types';
import { type ReactNode } from 'react';

import { type ConversationalMMTrackEvent } from '../analytics/events';
import { ConversationalMMProvider } from '../context/ConversationalMMContext';
import { type TerminalSlideAction } from '../types';
import FullscreenResultsContentLegacy from './FullscreenContentResultsLegacy';
import FullscreenResultsContent from './FullscreenResultsContent';

export interface ConversationalMMFullscreenResultsProps {
    results: OnboardingChatResults;
    onResultsChange?: (results: OnboardingChatResults) => void;
    /**
     * Which CTA renders on the terminal slide (Goals for the default branch,
     * InvestingCta for the investing branch). Required — always make an
     * explicit choice rather than relying on `onClose` presence to infer it.
     */
    terminalAction: TerminalSlideAction;
    /** Wires the close (X) button; only rendered when `showCloseButton` is true. */
    onClose?: () => void;
    showCloseButton?: boolean;
    /** Skip the "Cultivating…" hold screen (used for non-signup entry points). */
    skipCultivating?: boolean;
    /** Skip AhaMoment's entering animation (used when expanding from the home tile). */
    skipEnteringAnimation?: boolean;
    showGuidedTooltips?: boolean;
    hasSeenGuidedTooltips?: boolean;
    onGuidedTooltipsSeen?: () => void;
    onTrackEvent?: ConversationalMMTrackEvent;
    useNewArtifact?: boolean;
    useGradientBackground?: boolean;
    guides?: Guide[];
    cmmFinalArtifactSegment?: CmmArtifactFinalPageExperiment_ExperimentParam_Value;
    /** Optional render prop for account creation form on the Summary step. */
    renderAccountForm?: () => ReactNode;
}

export const ConversationalMMFullscreenResults = ({
    results,
    onResultsChange,
    terminalAction,
    onClose,
    showCloseButton = false,
    skipCultivating = false,
    skipEnteringAnimation = false,
    showGuidedTooltips = true,
    hasSeenGuidedTooltips = false,
    onGuidedTooltipsSeen,
    onTrackEvent,
    useNewArtifact = false,
    useGradientBackground = false,
    guides = [],
    cmmFinalArtifactSegment,
    renderAccountForm,
}: ConversationalMMFullscreenResultsProps) => {
    return (
        <ConversationalMMProvider
            results={results}
            onResultsChange={onResultsChange}
            terminalAction={terminalAction}
            showGuidedTooltips={showGuidedTooltips}
            hasSeenGuidedTooltips={hasSeenGuidedTooltips}
            onGuidedTooltipsSeen={onGuidedTooltipsSeen}
            onTrackEvent={onTrackEvent}
            guides={guides}
            cmmFinalArtifactSegment={cmmFinalArtifactSegment}
            renderAccountForm={renderAccountForm}
        >
            {useNewArtifact ? (
                <FullscreenResultsContent
                    skipCultivating={skipCultivating}
                    onClose={onClose}
                    showCloseButton={showCloseButton}
                    useGradientBackground={useGradientBackground}
                />
            ) : (
                <FullscreenResultsContentLegacy
                    onClose={onClose}
                    showCloseButton={showCloseButton}
                    skipCultivating={skipCultivating}
                    skipEnteringAnimation={skipEnteringAnimation}
                />
            )}
        </ConversationalMMProvider>
    );
};

export default ConversationalMMFullscreenResults;
